import * as React from 'react';
import classNames from 'classnames';
import { isNumber } from 'lodash-es';
import { AbstractProps } from '@/types';
import { AppContext } from '@/utils/appProvider';
import Symbol from './Symbol';
import './style/index.less';

export type SizeType = 'default' | 'large' | 'middle' | 'small' | undefined;
export type IconSize = SizeType | number | string | number[];
export type IconType = 'primary' | 'normal' | 'danger' | 'unset' | undefined;

export interface ComponentProps extends AbstractProps {
  ['aria-hidden']?: string;
  fill: string;
  height: string | number;
  viewBox?: string;
  width: string | number;
}

export interface IconProps extends AbstractProps {
  component?: React.ComponentType<ComponentProps>;
  fontFamily?: string;
  mode?: 'font-class' | 'symbol';
  name: string;
  prefix?: string;
  size?: IconSize;
  spin?: boolean;
  theme?: string;
  twoToneColor?: string;
  type?: IconType;
  onClick?: React.MouseEventHandler<HTMLElement>;
  loading?: boolean;
}

const Icon: React.FC<IconProps> = (props) => {
  const { prefixCls: appPrefixCls, getPrefixCls } = React.useContext(AppContext);
  const {
    children,
    className,
    component: Component,
    disabled,
    fontFamily = 'iconfont',
    mode = 'font-class',
    name,
    prefix = `${appPrefixCls}-icon`,
    prefixCls,
    size: propSize,
    spin,
    style = {},
    theme,
    type,
    loading,
    ...rest
  } = props;
  let size = propSize;

  if (isNumber(size) && !style.fontSize) {
    if (size >= 12) {
      style.fontSize = size;
    }
    if (mode === 'symbol') {
      style.width = size;
      style.height = size;
    }
    size = `${size}`;
  }
  if (Array.isArray(size) && mode === 'symbol') {
    style.width = size.length > 0 ? size[0] : undefined;
    style.height = size.length > 1 ? size[1] : undefined;
  }

  const classString = classNames(
    {
      [fontFamily]: fontFamily,
      [prefix]: prefix,
      [name]: name,
      'spin-icon': spin,
    },
    className
  );

  if (mode === 'symbol') {
    return (
      <Symbol className={classNames(classString, 'svg-icon')} name={name} style={style} {...rest} />
    );
  }

  return (
    <i className={classString} style={style} {...rest}>
      {children}
    </i>
  );
};

function addLoadingProp(component: React.FC<IconProps>) {
  return (props: IconProps) => {
    if (!props.loading) {
      return component(props);
    }
    const newProps = { ...props, name: 'icon-loading_w_nor', spin: true };
    return component(newProps);
  };
}

export default addLoadingProp(Icon);
